import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import {
  CareItem,
  CareItemType,
  getCareItems,
} from 'src/replicant/buruburu/care/care.ruleset';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import CareItemButton from '../components/CareItemButton';
import sounds from 'src/lib/sounds';
import { Pet, PetEvolutionStage } from 'src/replicant/buruburu/pet';
import ScrollBasic from '../components/shared/ScrollBasic';
import StateObserver from 'src/StateObserver';
import { Visit } from 'src/replicant/visit';
import { getFriendsPet } from '../pet';

// Group Care Items in pairs so we can reuse existing scroll components
type CareItemPair = [CareItem, CareItem?];
type CloseHandler = (result?: CareItem['id']) => void;

export default class PopupSelectCareItem extends PopupBasic {
  private careItems: ScrollBasic<CareItemPair>;
  private buttonBack: ButtonScaleViewWithText;
  private close: CloseHandler;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 815,
      darkerBg: false,
      tracking: { feature: 'care', $subFeature: 'popup-careitem' },
      titleIcon: {
        image: 'assets/ui/buruburu/care/care_firstAid_icon.png',
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      width: this.box.style.width - 80,
      height: 50,
      x: 40,
      y: 80,
      zIndex: 1,
    });

    this.careItems = new ScrollBasic<CareItemPair>({
      superview: this.box,
      createItem: this.createItem.bind(this),
      showBg: true,
      rect: {
        x: 30,
        y: 150,
        width: this.box.style.width - 60,
        height: 500,
      },
    });

    this.buttonBack = new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.box,
      labelOffsetY: -1,
      fontSize: 31,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: this.box.style.height - 45 - 80 / 2,
      width: 259,
      height: 80,
      centerOnOrigin: true,
      localeText: () => i18n('basic.back'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close();
      },
      tracking: { feature: 'care', $subFeature: 'popup-careitem-back' },
    });
  }

  public init({
    type,
    stage,
  }: {
    type: CareItemType;
    stage: PetEvolutionStage;
  }) {
    const stringType = type === 'first_aid' ? 'firstAid' : type;
    super.init({
      title: i18n(`careAction.${stringType}`),
      titleIcon: {
        image: `assets/ui/buruburu/care/care_${stringType}_icon.png`,
        height: stringType === 'snack' ? 75 : 55,
      },
      message: i18n('careItem.selectMessage'),
    });

    this.careItems.setItems(this.getCareItems(type, stage));
  }

  /**
   * Return care items list
   */
  private getCareItems(type: CareItemType, stage: PetEvolutionStage) {
    const { user } = StateObserver.getState();
    const now = StateObserver.now();
    const isVisiting = Visit.getters(user).isVisiting();
    const isDying = Pet.getters(user).getPetState(now, isVisiting) === 'dying';
    const isFriendDying = isVisiting && getFriendsPet().status === 'dying';

    // Perfume filter
    // Show only perfume if player pet or friend pet is dying
    const onlyPerfume = (item: CareItem) => {
      if (isDying || isFriendDying) {
        return item.id === 'fa_perfume' || item.id === 'fa_superPerfume';
      } else {
        return item.id !== 'fa_perfume' && item.id !== 'fa_superPerfume';
      }
    };

    const items = getCareItems(type, stage)
      .filter((item) => onlyPerfume(item))
      .sort((a, b) => b.priority - a.priority)
      .reduce((result, _, i, array) => {
        if (i % 2 === 0) {
          result.push(array.slice(i, i + 2));
        }
        return result;
      }, []);

    return items;
  }

  private createItem(
    superview: View,
    _: number,
    careItems: [CareItem, CareItem?],
  ) {
    const row = superview.addSubview(
      new FlexBoxView({
        width: superview.style.width,
        height: uiConfig.buruburu.care.itemButton.container.height,
        flex: {
          justifyContent: 'space-evenly',
        },
      }),
    );
    return careItems.reduce((container, careItem) => {
      container.addSubview(
        new CareItemButton({
          careItem,
          onClick: async () => {
            sounds.sfx.play('ok');
            return this.close(careItem.id);
          },
        }),
      );
      return container;
    }, row);
  }
}
